<template>
  <div class="carousel-container">
    <!-- 1st section -->
    <div class="carousel-slide enhance-container" :class="{ active: currentSlide === 0 }">
      <div class="slide-content">
        <h2>Enhancing Digital Collections with Cualitywork</h2>
        <p>
          At Cualitywork, we continually enhance our platform to empower financial institutions with
          the latest in digital collection technology. Our app now supports advanced features
          designed to elevate your field operations for efficiency, compliance, and measurable
          results.
        </p>

        <div class="feature-box">
          <div class="icon">
            <img
              src="@/assets/img/carousel/headset.svg"
              alt="Headset Icon"
              class="colorable-icon"
            />
          </div>
          <div class="feature-content">
            <h4>Integrated Communications</h4>
            <p>
              Effortlessly connect with borrowers using our built-in dialer, SMS, WhatsApp, and
              voice calls — all from a single platform to maximize contact rates.
            </p>
          </div>
        </div>

        <div class="feature-box">
          <div class="icon">
            <img
              src="@/assets/img/carousel/passport.svg"
              alt="Passport Icon"
              class="colorable-icon"
            />
          </div>
          <div class="feature-content">
            <h4>Smart Case Management</h4>
            <p>
              Dynamically filter, sort, and prioritize cases to focus agents on high-value and
              overdue accounts, optimizing workload distribution.
            </p>
          </div>
        </div>

        <div class="feature-box">
          <div class="icon">
            <img
              src="@/assets/img/carousel/location-dot.svg"
              alt="Location Icon"
              class="colorable-icon"
            />
          </div>
          <div class="feature-content">
            <h4>Visit Tracking & Verification</h4>
            <p>
              Capture geotagged timestamps for every field visit, ensuring transparency, compliance,
              and informed follow-ups.
            </p>
          </div>
        </div>

        <div class="feature-box">
          <div class="icon">
            <img src="@/assets/img/carousel/dice-two.svg" alt="Dice Icon" class="colorable-icon" />
          </div>
          <div class="feature-content">
            <h4>Seamless API Integration</h4>
            <p>
              Connect Cualitywork with any existing collection CRM or backend system using robust
              APIs for unified workflows and data synchronization.
            </p>
          </div>
        </div>

        <div class="feature-box">
          <div class="icon">
            <img
              src="@/assets/img/carousel/arrow-up-arrow-down.svg"
              alt="Arrow Icon"
              class="colorable-icon"
            />
          </div>
          <div class="feature-content">
            <h4>Performance Monitoring</h4>
            <p>
              Access real-time dashboards and downloadable Excel reports to summarize agent
              performance and drive continuous improvement.
            </p>
          </div>
        </div>

        <div class="feature-box">
          <div class="icon">
            <img src="@/assets/img/carousel/cloud.svg" alt="Cloud Icon" class="colorable-icon" />
          </div>
          <div class="feature-content">
            <h4>Offline Support & Scheduling</h4>
            <p>
              Field agents can capture data without connectivity, with automated syncing when
              online, plus future visit scheduling and reminders.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- 2nd section -->
    <div class="carousel-slide enhance-container" :class="{ active: currentSlide === 1 }">
      <div class="slide-content">
        <h1 class="fieldforce-heading">Real-Time Field Force Management</h1>
        <div class="second-section-container">
          <div class="images-container">
            <img
              src="https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/lcq3ejbz8hhrxta/generated-images/QKkmvGMyWeLjN1DzuVwNY.png"
              class="chakra-image css-zec7a"
              alt="Features"
            />
            <img
              alt=""
              src="https://imgproxy.gamma.app/resize/quality:80/resizing_type:fit/width:2000/https://cdn.gamma.app/lcq3ejbz8hhrxta/generated-images/1-LalGNkRePDz4St6frLq.png"
              class="chakra-image css-zec7a"
            />
          </div>

          <div class="second-section-text">
            <p>
              Gain complete visibility into your field operations with real-time GPS tracking,
              automated route optimization, and comprehensive activity monitoring. Whether managing
              sales teams, delivery personnel, or service technicians, our platform ensures maximum
              productivity and accountability.
            </p>
            <p>
              Dynamic task assignment capabilities allow managers to optimize field operations on
              the fly, while automated reporting provides insights into team performance, customer
              interactions, and operational efficiency. Integration with mapping services ensures
              your teams always take the most efficient routes, reducing fuel costs and improving
              customer service.
            </p>

            <div class="location-features">
              <img
                src="@/assets/img/carousel/reflect-both.svg"
                alt="Reflect Icon"
                class="colorable-icon"
              />
            </div>
            <div class="fieldforce-text">
              <h3>Task Assignment</h3>
              <p>Intelligent task distribution based on location, skills, and workload</p>
            </div>

            <div class="location-features">
              <img src="@/assets/img/carousel/route.svg" alt="Route Icon" class="colorable-icon" />
            </div>
            <div class="fieldforce-text">
              <h3>Route Optimization</h3>
              <p>AI-powered routing reduces travel time and operational costs</p>
            </div>

            <div class="location-features">
              <img src="@/assets/img/carousel/bug.svg" alt="Bug Icon" class="colorable-icon" />
            </div>
            <div class="fieldforce-text">
              <h3>Real-Time Reporting</h3>
              <p>Instant visibility into field activities, customer visits, and task completion</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3rd section -->
    <div class="carousel-slide enhance-container" :class="{ active: currentSlide === 2 }">
      <div class="slide-content">
        <div class="third-section-container">
          <div class="text-content">
            <h1 class="fieldforce-heading">Advanced Attendance & Leave Management</h1>
            <p>
              Eliminate buddy punching and attendance discrepancies with our multi-modal attendance
              tracking system. From biometric scanners to geo-fenced mobile check-ins, ensure
              accurate time tracking regardless of your work environment.
            </p>

            <div class="feature">
              <img
                src="@/assets/img/carousel/fingerprint.svg"
                alt="Fingerprint Icon"
                class="colorable"
              />
              <div>
                <h3>Face Clock-in with added QR code based verification</h3>
                <p>
                  Combines two layers of authentication for maximum accuracy and fraud prevention
                </p>
              </div>
            </div>

            <div class="feature">
              <img
                src="@/assets/img/carousel/location-crosshairs.svg"
                alt="Location Crosshairs Icon"
                class="colorable"
              />
              <div>
                <h3>Geo-Fencing</h3>
                <p>
                  Location-based check-ins ensure employees are at designated work sites during
                  clock-in
                </p>
              </div>
            </div>

            <div class="feature">
              <img src="@/assets/img/carousel/calendar.svg" alt="Calendar Icon" class="colorable" />
              <div>
                <h3>Smart Scheduling</h3>
                <p>
                  Automated roster management with shift planning, overtime calculation, and holiday
                  management
                </p>
              </div>
            </div>

            <div class="feature">
              <img src="@/assets/img/carousel/mobile.svg" alt="Mobile Icon" class="colorable" />
              <div>
                <h3>Mobile Access</h3>
                <p>
                  Employee self-service portal for leave applications, attendance viewing, and shift
                  swapping
                </p>
              </div>
            </div>
          </div>

          <div class="image-wrapper">
            <img src="@/assets/img/advance.png" alt="App Interface" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginCarousel',
  data() {
    return {
      currentSlide: 0,
      slideInterval: null
    }
  },
  mounted() {
    this.startInfiniteSlideshow()
  },
  beforeDestroy() {
    if (this.slideInterval) {
      clearInterval(this.slideInterval)
    }
  },
  methods: {
    startInfiniteSlideshow() {
      this.slideInterval = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % 3
      }, 2000)
    }
  }
}
</script>

<style scoped>
.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 95%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-slide.active {
  opacity: 1;
  z-index: 2;
}

.slide-content {
  width: 750px;
  max-width: 100%;
}

/* .enhance-container {
  background: #fff;
  border-radius: 10px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  padding: 20px 40px;
} */
.chakra-image {
  width: 340px;
  height: 340px;
}

h2 {
  font-size: 22px;
  font-weight: 700;
  color: #1a2b49;
  margin-bottom: 10px;
}
h3 {
  font-weight: bold;
}

p {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.feature-box {
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid #e1e7f5;
  border-radius: 8px;
  margin-bottom: 15px;
  background: #fff;
}

.icon {
  font-size: 18px;
  color: #1e3063;
  background-color: #cee6fd;
  padding-bottom: 10px;
  width: 42px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
}

.light-blue-bg {
  background-color: #add8e6 !important;
}

.feature-content {
  padding: 12px;
}

.feature-content h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1a2b49;
}

.feature-content p {
  margin: 3px 0 0;
  font-size: 13px;
  color: #555;
  line-height: 1.4;
}

/* Second section styles */
.second-section-container {
  display: flex;
  flex-direction: row;
}

.images-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 20px;
}

.images-container img {
  width: 100%;
  height: 48%;
  object-fit: cover;
  border-radius: 8px;
}

.second-section-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.fieldforce-heading {
  font-size: 24px;
  font-weight: bold;
  text-align: left;
  margin-top: 0;
  margin-bottom: 20px;
}

.fieldforce-features {
  margin-top: 25px;
}

.feature-item {
  display: flex;
  margin-bottom: 20px;
}

.location-features {
  background-color: #cee6fd;
  color: #1e3063;
  padding: 15px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: polygon(95% 0%, 100% 50%, 95% 95%, 0% 100%, 3% 55%, 0% 0%);
  flex-shrink: 0;
  width: 100%;
  height: 50px;
}

.fieldforce-text {
  margin-left: 15px;
}

.fieldforce-text h3 {
  margin: 0;
  font-size: 16px;
  font-weight: bold;
  color: #1d2c55;
}

.fieldforce-text p {
  font-size: 14px;
  color: #475569;
  margin: 5px 0 0;
}

/* Third section styles */
.third-section-container {
  display: flex;
}

.text-content {
  flex: 1;
  width: calc(100% - 20px);
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

.colorable {
  /* Using filter to change the color appearance of images with bolder color */
  filter: brightness(0) saturate(100%) invert(65%) sepia(50%) saturate(1000%) hue-rotate(180deg);
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.image-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.colorable-icon {
  font-size: 1em;
  width: 23.61px;
  height: 23.61px;
}

.image-wrapper img {
  -webkit-mask-image: url('https://assets.gammahosted.com/i7tuk8wdp/_next/static/media/wiggle-right.8ee6fc0f.svg');
  mask-image: url('https://assets.gammahosted.com/i7tuk8wdp/_next/static/media/wiggle-right.8ee6fc0f.svg');
  mask-size: cover;
  mask-repeat: no-repeat;
  mask-position: left;
  width: 85%;
  margin-left: -5%;
}
</style>
